import { accountIdentity, accountSearchText, twinHints } from '../../../utilities/accountIdentity.js';

/** Category_Group's types, as available_categories gives them in `group_type`. */
const TYPE_INCOME = 2;
const TYPE_SAVINGS = 3;
const TYPE_LOANS = 4;

/**
 * The editor's conditions as Import_Domain::rule_preview takes them: only those filled in, the amount
 * in cents. With none it is `{}`, which still answers every condition's values for the pickers.
 */
export function previewParams({ account, category, noCategory, merchant, statement, amountCents }) {
	const params = {};
	if(account.trim()) {
		params.external_account = account.trim();
	}
	if(noCategory) {
		params.no_category = 1;
	} else if(category.trim()) {
		params.external_category = category.trim();
	}
	if(merchant.trim()) {
		params.external_merchant = merchant.trim();
	}
	if(statement.trim()) {
		params.statement_contains = statement.trim();
	}
	if(amountCents !== null && amountCents !== undefined) {
		params.amount = amountCents;
	}
	return params;
}

/**
 * A facet's values as picker options, `{ value, label, count }`, with `current` added (no count) when
 * the facet does not have it: a saved rule can name a value no fetched row has any more.
 */
export function facetOptions(facet, current = '') {
	const options = (facet || []).map((entry) => ({ value: String(entry.value), label: String(entry.value), count: entry.count }));
	const wanted = (current || '').trim();
	if(wanted && !options.some((option) => option.value === wanted)) {
		options.unshift({ value: wanted, label: wanted, count: null });
	}
	return options;
}

/**
 * The account condition's options, grouped by the institution of the account each Monarch name is
 * mapped to, so a picker can draw the budget's account for it (AccountLabel) beside the name the rule
 * matches on. Names come from the facet (rows that match the other conditions, with counts) and from
 * the account maps (`accountRules`, with none); a name mapped to no account, or skipped, is grouped
 * under "Not mapped".
 *
 * Each option is `{ value, label, count, identity, mapAction }`; `label` carries what a search
 * should find, the Monarch name and the account's own words.
 */
export function accountRuleOptions(facet, accountRules, availableAccounts, current = '') {
	const counts = new Map((facet || []).map((entry) => [String(entry.value), entry.count]));
	const names = [...counts.keys()];
	(accountRules || []).forEach((map) => {
		if(!counts.has(map.external_key)) {
			names.push(map.external_key);
		}
	});
	const wanted = (current || '').trim();
	if(wanted && !names.includes(wanted)) {
		names.unshift(wanted);
	}

	const accounts = availableAccounts || [];
	const hints = twinHints(accounts);
	const groups = [];
	const byLabel = {};
	const unmapped = { label: 'Not mapped to an account', badge: null, options: [] };
	names.forEach((name) => {
		const map = (accountRules || []).find((candidate) => candidate.external_key === name) || null;
		const account = map && map.action === 'assign' && map.plaid_account_id
			? accounts.find((candidate) => Number(candidate.id) === Number(map.plaid_account_id))
			: null;
		const identity = account ? accountIdentity(account, null, hints.get(account.id)) : null;
		const option = {
			value: name,
			label: identity ? `${name} ${accountSearchText(identity)}` : name,
			count: counts.has(name) ? counts.get(name) : null,
			identity,
			mapAction: map ? map.action : null,
		};
		if(!identity) {
			unmapped.options.push(option);
			return;
		}
		const groupLabel = identity.institutionName || 'Other accounts';
		if(!byLabel[groupLabel]) {
			byLabel[groupLabel] = { label: groupLabel, badge: identity.badge, options: [] };
			groups.push(byLabel[groupLabel]);
		}
		byLabel[groupLabel].options.push(option);
	});
	return unmapped.options.length ? [...groups, unmapped] : groups;
}

/** The option among grouped `options` whose value is `value`, or null. */
export function findGroupedOption(groups, value) {
	for(const group of groups || []) {
		const found = (group.options || []).find((option) => option.value === value);
		if(found) {
			return found;
		}
	}
	return null;
}

/**
 * Whether a row of the export's amount `cents` would count toward its category or against it, the
 * way Import_Amount_Converter files it: income keeps the export's sign, every other group flips it,
 * no category (`groupType` null) keeps it, and `reverseSign` flips the result. Toward is a positive
 * ledger amount: spending on an expense, money in on income, a payment on a loan.
 */
export function countsToward(cents, groupType, reverseSign) {
	let ledger = groupType === null || groupType === TYPE_INCOME ? cents : -cents;
	if(reverseSign) {
		ledger = -ledger;
	}
	return ledger > 0;
}

/**
 * A hint about the sign, from which way the matched rows' money goes, or null.
 *
 * Reversing is for a credit on a loan or card's own account that is really a payment toward it, so
 * it is only suggested where that can be: the outcome a Loans or Savings category, or the account
 * condition a liability. A refund on an expense also runs against its category, and that is right,
 * so it is no reason to suggest anything. Either way, with the sign reversed and most rows then
 * counting against the category, that is said, since it is the setting's usual mistake.
 *
 * `preview` is rule_preview's answer; `category` the outcome's available_categories entry, null for any other outcome;
 * `accountIsLiability` whether the account condition names a loan or credit account.
 *
 * @returns {?{ against: number, total: number, suggestReverse: boolean, text: string }}
 */
export function signHint({ preview, category, accountIsLiability = false, reverseSign }) {
	/** With no category there is no direction to count toward; its sign alone says spending or income. */
	if(!preview || !preview.row_count || !category) {
		return null;
	}
	const groupType = category.group_type;
	const credits = preview.credit_count || 0;
	const debits = preview.debit_count || 0;
	const total = credits + debits;
	if(!total) {
		return null;
	}
	/** Every row of one sign counts the same way, so the counts settle it without the rows themselves. */
	const against = (countsToward(1, groupType, reverseSign) ? 0 : credits) + (countsToward(-1, groupType, reverseSign) ? 0 : debits);
	if(against * 2 <= total) {
		return null;
	}
	const name = category.name;
	const rows = `${against === total ? (total === 1 ? 'This row' : `All ${total} of these rows`) : `${against} of these ${total} rows`}`;
	if(reverseSign) {
		return {
			against,
			total,
			suggestReverse: false,
			text: `${rows} would count against ${name} with the sign reversed.`,
		};
	}
	const loanLike = groupType === TYPE_LOANS || groupType === TYPE_SAVINGS || accountIsLiability;
	if(!loanLike) {
		return null;
	}
	return {
		against,
		total,
		suggestReverse: true,
		text: `${rows} would count against ${name}. A credit on a loan or card's own account is usually a payment toward it.`,
	};
}
